import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { platformApi } from "@/app/platform";
import { ButtonBase } from "@/components/ButtonBase/ButtonBase";
import { IconESim, IconFlightService } from "@/components/Icons";
import { CaretRightIcon } from "@/components/Icons/rtlReadyIcons";
import { CabinClass, TripType } from "@/features/flights/types/types";
import type { HotelSearchFormValues } from "@/features/hotels/utils/hotelsValidation";
import type { FindUserReservationExternalHotelReservation } from "@/types/codegen";

type EssentialCardProps = {
  title: string;
  subtitle: string;
  icon: ReactNode;
  background?: "accent" | "white";
  onClick: () => void;
};

const EssentialCard = ({
  title,
  subtitle,
  icon,
  background = "white",
  onClick,
}: EssentialCardProps) => {
  return (
    <ButtonBase
      onClick={onClick}
      className={clsx(
        "flex items-center gap-1 rounded-2xl border border-stroke-neutral-soft p-3",
        {
          "bg-white": background === "white",
          "bg-core-accent-100": background === "accent",
        },
      )}
    >
      {icon}
      <div className="space-y-0.5 text-start">
        <div className="font-medium text-black">{title}</div>
        <div className="text-sm text-text-placeholder">{subtitle}</div>
      </div>
      <CaretRightIcon className="me-auto size-4 shrink-0" />
    </ButtonBase>
  );
};

type Props = {
  reservation: FindUserReservationExternalHotelReservation;
  destination?: HotelSearchFormValues["destination"];
};

export const TripEssentials = ({ reservation, destination }: Props) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const handleFlightSuggestionClick = () => {
    if (!destination) return;

    const travellers = reservation.travelers;
    const { adult, child, infant } = travellers.reduce(
      (acc, traveller) => {
        return { ...acc, [traveller.type]: acc[traveller.type] + 1 };
      },
      { adult: 0, child: 0, infant: 0 },
    );
    const formData = {
      adult,
      child,
      infantWithoutSeat: infant,
      cabinClass: CabinClass.ANY,
      tripType: TripType.ROUND_TRIP,
      dates: {
        departure: dayjs(reservation.serviceDates.startDate).toDate(),
        return: dayjs(reservation.serviceDates.endDate).toDate(),
      },
      to: destination,
      from: null,
    };
    navigate({
      to: "/flights/search",
      state: (prev) => ({
        ...prev,
        flightSearchPrefill: formData,
      }),
    });
  };

  return (
    <div className="section-card space-y-4">
      <div className="text-lg font-medium text-text-primary">
        {intl.formatMessage({
          defaultMessage: "Your trip essentials",
          description: "Trip essentials title on hotel booking success page",
        })}
      </div>
      {useFeatureIsOn("e-sim-navigation-link") ? (
        <EssentialCard
          title={intl.formatMessage({
            defaultMessage: "Skip the roaming fees",
            description: "Esim essential card title",
          })}
          subtitle={intl.formatMessage(
            {
              defaultMessage:
                "Activate e-SIM in one tap and stay connected in {city}",
              description: "Esim essential card subtitle",
            },
            {
              city: destination?.name,
            },
          )}
          icon={<IconESim className="size-10 shrink-0" />}
          background="accent"
          onClick={() => platformApi.navigateToMiniApp("3488020210984303")}
        />
      ) : null}
      {destination ? (
        <EssentialCard
          title={intl.formatMessage(
            {
              defaultMessage: "Book a flight to {city}",
              description: "Flight suggestion essential card title",
            },
            {
              city: destination?.name,
            },
          )}
          subtitle={intl.formatMessage(
            {
              defaultMessage:
                "We found flights to {city} that match your hotel dates",
              description: "Flight suggestion essential card subtitle",
            },
            {
              city: destination?.name,
            },
          )}
          icon={<IconFlightService className="size-10 shrink-0" />}
          onClick={handleFlightSuggestionClick}
        />
      ) : null}
    </div>
  );
};
